import type { Nullable } from 'corgi-types';

import type { ValidateValidatorType } from '../../enums/ValidatorType';
import { LengthValidatorType } from '../../enums/ValidatorType';
import type { Translate } from '../../providers/LocaleProvider';
import type {
  FieldValidationMap,
  FormData,
  GetValidationErrorKey,
  ValidateMap,
} from '../../types/form';
import { isLengthValid, isValidatorOptional, validateField } from '../../utils/form/validation';
import { getInvalidCharErrorDetails } from '../../utils/form/validation/allowed-characters';

interface GetValidationErrorKeysOptions<FieldName extends string> {
  field: FieldName;
  value: string;
  formData: FormData<FieldName>;
  validator: FieldValidationMap<FieldName>;
  getValidationErrorKey?: GetValidationErrorKey<FieldName>;
}

interface GetValidationErrorsOptions<FieldName extends string> {
  field: FieldName;
  value: string;
  formData: FormData<FieldName>;
  validator: Nullable<FieldValidationMap<FieldName>>;
  translate: Translate;
  getValidationErrorKey?: GetValidationErrorKey<FieldName>;
}

type GetValidationErrors = <FieldName extends string>(
  options: GetValidationErrorsOptions<FieldName>
) => string[];

type GetValidationStateOptions<FieldName extends string> = {
  field: FieldName;
  value: string;
  formData: FormData<FieldName>;
  getValidationErrorKey?: GetValidationErrorKey<FieldName>;
  translate: Translate;
  validator: Nullable<FieldValidationMap<FieldName>>;
};

const getInvalidatedProperties = <FieldName extends string>(
  validateFns: ValidateMap<FieldName>,
  value: string,
  formData: FormData<FieldName>
) => {
  const invalidatedFns: ValidateValidatorType[] = [];

  for (const validateFnProperty in validateFns) {
    if (!validateFns[validateFnProperty](value, formData)) {
      invalidatedFns.push(validateFnProperty as ValidateValidatorType);
    }
  }

  return invalidatedFns;
};

const getValidationErrorKeys = <FieldName extends string>({
  field,
  value,
  formData,
  validator,
  getValidationErrorKey,
}: GetValidationErrorKeysOptions<FieldName>) => {
  if (!isLengthValid(validator, value)) {
    const lengthErrorKey = getValidationErrorKey(field, LengthValidatorType.Length);

    return [lengthErrorKey];
  } else if (validator?.validate) {
    const listOfErrors: string[] = [];
    const invalidatedProperties = getInvalidatedProperties(validator.validate, value, formData);
    for (const invalidatedFn of invalidatedProperties) {
      const errorKey = getValidationErrorKey(field, invalidatedFn);
      listOfErrors.push(errorKey);
    }

    return listOfErrors;
  }

  return [];
};

const getValidationErrors: GetValidationErrors = ({
  field,
  value,
  formData,
  validator,
  translate,
  getValidationErrorKey,
}) => {
  const optional = isValidatorOptional(validator, value);

  if (!getValidationErrorKey || optional) {
    return [];
  }

  const errorKeys = getValidationErrorKeys({
    field,
    value,
    formData,
    validator,
    getValidationErrorKey,
  });

  const invalidCharErrorDetails = getInvalidCharErrorDetails(field, value);

  return errorKeys.map(errorKey =>
    invalidCharErrorDetails
      ? `${translate(errorKey)} ${translate(
          'h24_validation_error_form_invalid_characters'
        )}: ${invalidCharErrorDetails}.`
      : translate(errorKey)
  );
};

export const getValidationState = <FieldName extends string>({
  field,
  value,
  formData,
  getValidationErrorKey,
  translate,
  validator,
}: GetValidationStateOptions<FieldName>) => {
  const valid = validateField({
    value,
    validator,
    formData,
  });

  const errors = getValidationErrors({
    field,
    value,
    formData,
    validator,
    getValidationErrorKey,
    translate,
  });

  return { valid, errors };
};
